import { useQuery } from '@apollo/client';
import { faGlobe, faServer } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FormField from '../../core/components/FormField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import {
    SFTP_KNOWLEDGE_SOURCE_DEFAULT,
    SHAREPOINT_TOOL_PROVIDER_DEFAULT,
    SharePointToolProviderConfig,
    SftpKnowledgeSourceConfig,
    SourceSyncInfo,
} from '../../products/assistants/components/config/ConfigSteps.tsx';
import {
    KnowledgeUrlItems,
    KnowledgeUrlStatus,
    UrlSyncedFile,
} from '../../products/assistants/components/knowledge/KnowledgeUrlItems.tsx';
import { SftpKnowledgeSourceCard } from '../../products/assistants/components/knowledge/SftpKnowledgeSourceCard.tsx';
import { SharePointProviderCard } from '../../products/assistants/components/knowledge/SharePointProviderCard.tsx';
import { SourceSyncStatusLine } from '../../products/assistants/components/knowledge/SourceSyncStatusLine.tsx';
import { GET_AVAILABLE_SFTP_CONFIGS } from '../../products/assistants/queries.ts';
import { canSeeSupportUserConfigs } from '../../users/utils.ts';

const SftpIcon = withIcon(faServer);
const UrlIcon = withIcon(faGlobe);

const SourceRow = ({
    icon,
    title,
    subtitle,
    sync,
}: {
    icon: React.ReactNode;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    sync?: SourceSyncInfo | null;
}) => (
    <div className="flex w-full items-start gap-3 rounded border border-solid border-line-weak p-3 shadow-sm">
        <span className="mt-0.5 flex-none text-content-medium">{icon}</span>
        <div className="flex min-w-0 flex-col gap-1">
            <span className="text-sm font-medium text-content-strong">{title}</span>
            {subtitle && <span className="break-all text-xs text-content-medium">{subtitle}</span>}
            <SourceSyncStatusLine config={sync} />
        </div>
    </div>
);

/** URL rows carry their own sync state and link to the synced file, like on the agent's knowledge tab. */
const useUrlSyncState = (project: any) => {
    const savedUrls = new Set<string>(project?.knowledgeUrls || []);
    const statuses = new Map<string, KnowledgeUrlStatus>(
        (project?.knowledgeUrlStatuses || []).map((status: KnowledgeUrlStatus) => [status.url, status])
    );
    const syncedFiles = new Map<string, UrlSyncedFile>(
        (project?.knowledgeFiles || [])
            .filter((file: any) => file.sourceType === 'url' && file.sourceItemId)
            .map((file: any) => [file.sourceItemId, { fileName: file.fileName, fileUrl: file.fileUrl }])
    );
    return { savedUrls, statuses, syncedFiles };
};

/** The project's connections — SharePoint or SFTP — at the top of the Knowledge card, as on the assistant's
 *  Knowledge page. Managers with the agents or integrations permission edit them with the agents' cards (the backend
 *  enforces the same rule); everyone else reads a summary built from the stored names, so no browsing rights are
 *  needed to see what is synced. */
export const ProjectKnowledgeConnections = ({
    form,
    canEditUrls = false,
    canConfigureSources = false,
}: {
    form: any;
    canEditUrls?: boolean;
    canConfigureSources?: boolean;
}) => {
    const { t } = useTranslation('chat');
    const { t: tAgents } = useTranslation('agents');
    const { user } = useApplicationContext();
    const isSupportUser = canSeeSupportUserConfigs(user);

    const sharepoint: SharePointToolProviderConfig | null = form.watch('sharepointKnowledgeSource');
    const sftp: SftpKnowledgeSourceConfig | null = form.watch('sftpKnowledgeSource');

    const { data: sftpConfigsData } = useQuery(GET_AVAILABLE_SFTP_CONFIGS, { skip: !sftp || canConfigureSources });
    const sftpConfig = (sftpConfigsData?.availableSftpConfigs || []).find(
        (config: { id: string }) => config.id === sftp?.sftpConfigId
    );
    const sftpLabel = sftpConfig
        ? [sftpConfig.user, sftpConfig.host].filter(Boolean).join('@') || tAgents('toolProviders.sftp.workistManaged')
        : null;

    // members who cannot change anything only see the block when there is something to see
    if (!canConfigureSources && !canEditUrls && !sharepoint && !sftp) return null;

    return (
        <FormField
            label={t('projects.knowledge.connections')}
            helpText={
                canConfigureSources
                    ? t('projects.knowledge.connectionsHelp')
                    : canEditUrls
                      ? t('projects.knowledge.connectionsAdminOnly')
                      : undefined
            }
        >
            <div className="flex flex-col gap-4">
                {canConfigureSources ? (
                    <>
                        <Controller
                            name="sharepointKnowledgeSource"
                            control={form.control}
                            render={({ field }) => (
                                <SharePointProviderCard
                                    isChecked={field.value !== null}
                                    disabled={sftp !== null}
                                    onIsCheckedChange={(checked) =>
                                        form.setValue(
                                            'sharepointKnowledgeSource',
                                            checked ? { ...SHAREPOINT_TOOL_PROVIDER_DEFAULT } : null,
                                            { shouldDirty: true }
                                        )
                                    }
                                    config={field.value}
                                    onConfigChange={(config) =>
                                        form.setValue('sharepointKnowledgeSource', config, { shouldDirty: true })
                                    }
                                />
                            )}
                        />
                        {(isSupportUser || sftp) && (
                            <Controller
                                name="sftpKnowledgeSource"
                                control={form.control}
                                render={({ field }) => (
                                    <SftpKnowledgeSourceCard
                                        isChecked={field.value !== null}
                                        disabled={sharepoint !== null}
                                        readOnly={!isSupportUser}
                                        onIsCheckedChange={(checked) =>
                                            form.setValue(
                                                'sftpKnowledgeSource',
                                                checked ? { ...SFTP_KNOWLEDGE_SOURCE_DEFAULT } : null,
                                                { shouldDirty: true }
                                            )
                                        }
                                        config={field.value}
                                        onConfigChange={(config) =>
                                            form.setValue('sftpKnowledgeSource', config, { shouldDirty: true })
                                        }
                                    />
                                )}
                            />
                        )}
                    </>
                ) : (
                    <>
                        {sharepoint && (
                            <SourceRow
                                icon={<img src="/images/external_logos/sharepoint.png" alt="" className="h-4 w-auto" />}
                                title={tAgents('toolProviders.sharepoint.name')}
                                subtitle={[
                                    [sharepoint.integrationName, sharepoint.siteName, sharepoint.driveName]
                                        .filter(Boolean)
                                        .join(' / '),
                                    (sharepoint.selectedItems || [])
                                        .map((item) => item.path?.join('/') || item.id)
                                        .join(', '),
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                sync={sharepoint}
                            />
                        )}
                        {sftp && (
                            <SourceRow
                                icon={<SftpIcon />}
                                title={tAgents('toolProviders.sftp.name')}
                                subtitle={[sftpLabel, sftp.path].filter(Boolean).join(' · ')}
                                sync={sftp}
                            />
                        )}
                        {!sharepoint && !sftp && (
                            <span className="text-sm text-content-weak">{t('projects.knowledge.noConnections')}</span>
                        )}
                    </>
                )}
            </div>
        </FormField>
    );
};

/** Web page URLs below the file rows, part of the knowledge list like on the assistant's Knowledge page: editable
 *  rows with "Add URL" for managers, read-only rows with sync state for everyone else. */
export const ProjectKnowledgeUrls = ({
    form,
    project,
    canEditUrls = false,
}: {
    form: any;
    project?: any;
    canEditUrls?: boolean;
}) => {
    const { savedUrls, statuses, syncedFiles } = useUrlSyncState(project);
    const urls: string[] = form.watch('knowledgeUrls') || [];

    if (canEditUrls) {
        return (
            <Controller
                name="knowledgeUrls"
                control={form.control}
                render={({ field }) => (
                    <KnowledgeUrlItems
                        urls={field.value || []}
                        onChange={field.onChange}
                        savedUrls={savedUrls}
                        statuses={statuses}
                        syncedFiles={syncedFiles}
                    />
                )}
            />
        );
    }

    return (
        <>
            {urls.map((url) => {
                const file = syncedFiles.get(url);
                const status = statuses.get(url);
                return (
                    <SourceRow
                        key={url}
                        icon={<UrlIcon />}
                        title={
                            <a
                                href={file?.fileUrl || url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-content-strong"
                            >
                                {file?.fileName || url}
                            </a>
                        }
                        subtitle={file ? url : undefined}
                        sync={{
                            syncStatus: status?.status || 'pending',
                            syncError: status?.error,
                            lastSyncedAt: status?.syncedAt,
                        }}
                    />
                );
            })}
        </>
    );
};
